import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { Calendar, User } from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import { type ContactUI } from "@/types/ui/customer-detail";
import { type Transaction as TransactionFull } from "@/types/transaction";

interface CreateCaseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCaseCreated?: () => void;
  prePopulatedCustomer?: {
    customerId: string;
    customerName: string;
    accountNumber: string;
  };
  selectedTransactions?: TransactionFull[];
}

interface NewCaseData {
  customerName: string;
  customerId: string;
  outstandingAmount: number;
  contactId: string;
  actionType: string;
  dueDate: string;
  notes: string;
}

export function CreateCaseModal({ 
  open, 
  onOpenChange, 
  onCaseCreated, 
  prePopulatedCustomer, 
  selectedTransactions: preSelectedTransactions 
}: Readonly<CreateCaseModalProps>) {
  const [isSaving, setIsSaving] = useState(false);
  const [contacts, setContacts] = useState<ContactUI[]>([]);

  // Form data state
  const [formData, setFormData] = useState<NewCaseData>({
    customerName: prePopulatedCustomer?.customerName || "",
    customerId: prePopulatedCustomer?.accountNumber || "",
    outstandingAmount: 0,
    contactId: "",
    actionType: "Follow-up call",
    dueDate: format(new Date(), "yyyy-MM-dd"),
    notes: "",
  });

  // Mock contacts for the customer
  const mockContacts: ContactUI[] = [
    {
      id: "contact-1",
      firstName: "John",
      surname: "Smith",
      email: "john.smith@example.com",
      phone: "+1 (555) 123-4567",
      jobTitle: "Accounts Payable Manager",
      company: "Example Corp",
      customerId: "1",
      address: "123 Business St, New York, NY 10001",
      notes: "Primary contact for all payment-related matters",
      createdAt: new Date(2023, 0, 15).toISOString(),
      lastContact: new Date(2024, 2, 20).toISOString(),
      isPrimary: true,
    },
    {
      id: "contact-2",
      firstName: "Sarah",
      surname: "Johnson",
      email: "sarah.johnson@example.com",
      phone: "+1 (555) 123-4568",
      jobTitle: "Finance Director",
      company: "Example Corp",
      customerId: "1",
      address: "123 Business St, New York, NY 10001",
      notes: "Escalation contact for urgent matters",
      createdAt: new Date(2023, 1, 10).toISOString(),
      lastContact: new Date(2024, 2, 15).toISOString(),
      isPrimary: false,
    },
    {
      id: "contact-3",
      firstName: "Michael",
      surname: "Chen",
      email: "michael.chen@example.com",
      phone: "+1 (555) 123-4569",
      jobTitle: "Accounts Payable Clerk",
      company: "Example Corp",
      customerId: "1",
      address: "123 Business St, New York, NY 10001",
      notes: "Day-to-day invoice queries",
      createdAt: new Date(2023, 3, 5).toISOString(),
      lastContact: new Date(2024, 2, 18).toISOString(),
      isPrimary: false,
    },
  ];

  // Calculate outstanding amount from selected transactions
  useEffect(() => {
    if (open && preSelectedTransactions && preSelectedTransactions.length > 0) {
      const totalOutstanding = preSelectedTransactions.reduce(
        (sum, t) => sum + (t.outstandingAmount || t.amount),
        0
      );
      
      setFormData(prev => ({
        ...prev,
        outstandingAmount: totalOutstanding,
      }));

      // Load mock contacts for the customer
      setContacts(mockContacts);
      
      // Auto-select primary contact if available
      const primaryContact = mockContacts.find(c => c.isPrimary);
      if (primaryContact) {
        setFormData(prev => ({
          ...prev,
          contactId: primaryContact.id,
        }));
      }
    }
  }, [open, preSelectedTransactions]);

  // Reset form when dialog closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setFormData({
        customerName: prePopulatedCustomer?.customerName || "",
        customerId: prePopulatedCustomer?.accountNumber || "",
        outstandingAmount: 0,
        contactId: "",
        actionType: "Follow-up call",
        dueDate: format(new Date(), "yyyy-MM-dd"),
        notes: "",
      });
      setContacts([]);
    } else {
      setContacts(mockContacts);
    }
    onOpenChange(open);
  };

  const validateForm = (): boolean => {
    if (!formData.customerName.trim() || !formData.customerId.trim()) {
      toast.error("Customer information is required");
      return false;
    }

    if (formData.outstandingAmount <= 0) {
      toast.error("Outstanding amount must be greater than zero");
      return false;
    }

    if (!formData.contactId) {
      toast.error("Please select a contact");
      return false;
    }

    if (!formData.actionType.trim()) {
      toast.error("Action type is required");
      return false;
    }

    if (!formData.dueDate) {
      toast.error("Due date is required");
      return false;
    }

    return true;
  };

  const handleSaveCase = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Here you would make the actual API call to create the case
      console.log("Creating new case:", {
        ...formData,
        selectedTransactions: preSelectedTransactions?.map(t => t.id),
      });

      toast.success("Collection case created successfully");
      
      // Call the callback to refresh the queue
      if (onCaseCreated) {
        onCaseCreated();
      }

      // Close the modal
      handleOpenChange(false);
    } catch (error) {
      toast.error("Failed to create case. Please try again.");
      console.error("Error creating case:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const getContactDisplayName = (contact: ContactUI) => {
    const name = `${contact.firstName} ${contact.surname}`;
    return contact.isPrimary ? `${name} (Primary)` : name;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle>Create Collection Case</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Create a new case for the selected transactions
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Customer (Read-only) */}
          <div className="space-y-2">
            <Label htmlFor="customer" className="flex items-center gap-1">
              <User className="h-3 w-3" />
              Customer
            </Label>
            <Input
              id="customer"
              value={`${formData.customerId} - ${formData.customerName}`}
              disabled
              className="bg-muted"
            />
          </div>

          {/* Outstanding Amount (Read-only) */}
          <div className="space-y-2">
            <Label htmlFor="amount">Outstanding Amount</Label>
            <Input
              id="amount"
              value={formatCurrency(
                formData.outstandingAmount,
                preSelectedTransactions?.[0]?.currency || 'GBP'
              )}
              disabled
              className="bg-muted font-semibold"
            />
          </div>

          {/* Contact */}
          <div className="space-y-2">
            <Label htmlFor="contact">
              Contact <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.contactId}
              onValueChange={(value) => setFormData({ ...formData, contactId: value })}
            >
              <SelectTrigger id="contact">
                <SelectValue placeholder="Select a contact" />
              </SelectTrigger>
              <SelectContent>
                {contacts.map((contact) => (
                  <SelectItem key={contact.id} value={contact.id}>
                    {getContactDisplayName(contact)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Action Type */}
          <div className="space-y-2">
            <Label htmlFor="actionType">
              Action Type <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.actionType}
              onValueChange={(value) => setFormData({ ...formData, actionType: value })}
            >
              <SelectTrigger id="actionType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Follow-up call">Follow-up call</SelectItem>
                <SelectItem value="Send email">Send email</SelectItem>
                <SelectItem value="Send letter">Send letter</SelectItem>
                <SelectItem value="Legal action">Legal action</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label htmlFor="dueDate" className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Due Date <span className="text-red-500">*</span>
            </Label>
            <Input
              id="dueDate"
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
            />
          </div>

          {/* Case Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Case Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any additional notes about this case..."
              rows={4}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSaveCase}
            disabled={isSaving}
            className="bg-primary hover:bg-primary/90"
          >
            {isSaving ? "Creating..." : "Create Case"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
